import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  People as UsersIcon,
  PersonAdd as NewUsersIcon,
  CheckCircle as ActiveUsersIcon,
  Block as BlockedUsersIcon,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const statsConfig = [
  {
    key: "total",
    title: "Total Users",
    description: "Total registered customer accounts",
    icon: <UsersIcon fontSize="large" color="primary" />,
    color: "#2563EB",
  },
  {
    key: "newToday",
    title: "New Today",
    description: "New signups in the last 24 hours",
    icon: <NewUsersIcon fontSize="large" sx={{ color: "#10B981" }} />,
    color: "#10B981",
  },
  {
    key: "active",
    title: "Active Users",
    description: "Active accounts currently enabled",
    icon: <ActiveUsersIcon fontSize="large" sx={{ color: "#8B5CF6" }} />,
    color: "#8B5CF6",
  },
  {
    key: "blocked",
    title: "Blocked Users",
    description: "Accounts currently disabled",
    icon: <BlockedUsersIcon fontSize="large" sx={{ color: "#EF4444" }} />,
    color: "#EF4444",
  },
];

function UserOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      setLoading(true);
      setError(null);

      try {
        const token =
          localStorage.getItem("adminToken") || localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/customers/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setStats(response.data || {});
      } catch (fetchError) {
        console.error("Error fetching user overview stats:", fetchError);
        setError(
          fetchError.response?.data?.message ||
            fetchError.message ||
            "Unable to load user overview",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: "100vh" }}>
      <Box mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          User Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          A compact view of your customer base and account health.
        </Typography>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Card sx={{ p: 3, bgcolor: "#FEF2F2", borderRadius: 3 }}>
          <Typography variant="h6" color="error.main" mb={1}>
            Unable to load user overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Card>
      ) : (
        <>
          <Grid container spacing={3} mb={3}>
            {statsConfig.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.key}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={3}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.secondary", fontWeight: 700 }}
                      >
                        {item.title}
                      </Typography>
                      <Tooltip title={item.description}>
                        <Box>{item.icon}</Box>
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        color: item.color,
                        lineHeight: 1,
                      }}
                    >
                      {stats[item.key] ?? 0}
                    </Typography>
                  </CardContent>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 20px rgba(15, 23, 42, 0.06)" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Notes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These counts are based on registered customer accounts only. Use
              the Customer List page to manage individual user records.
            </Typography>
          </Card>
        </>
      )}
    </Box>
  );
}

export default UserOverview;
